import { CountryName, getCountryIso2 } from './countryUtils';
import { GAEventName, sendGAEvent } from './gaUtils';
import { parseName } from './stringUtils';
import {
  FormField, FormFieldState, FormType
} from './types/form';
import { validateBusinessEmail } from './validators';

export const FastTrackFormType = {
  IT_TRIAL: 'IT Trial',
  PAYROLL: 'Payroll',
  SPEND: 'Spend',
} as const;

export type FastTrackType = (typeof FastTrackFormType)[keyof typeof FastTrackFormType];

export interface FastTrackPayload {
  companyHQCountry: string;
  companyName: string | string[];
  countryCode: string;
  email: string | string[];
  firstName: string | string[];
  hasRunPayroll: boolean;
  lastName: string | string[];
  nationalNumber: string | string[];
  numberOfPeople: number;
  questionnaireType: FastTrackType;
}

export function getFastTrackLastLeadOrigin(fastTrackFormType: FastTrackType) {
  return {
    [FastTrackFormType.IT_TRIAL]: '701Ud00000HIwF4IAL',
    [FastTrackFormType.PAYROLL]: 'Other_CORE_CAN_701Ud00000CQyjBIAT',
    [FastTrackFormType.SPEND]: '701Ud00000J2jkiIAB',
  }[fastTrackFormType || FastTrackFormType.PAYROLL];
}

export function getFastTrackThankYouPage(fastTrackType: FastTrackType) {
  const thankYouPages = {
    [FastTrackFormType.IT_TRIAL]: '/fasttrack/questionnaire',
    [FastTrackFormType.PAYROLL]: '/fasttrack/questionnaire',
    [FastTrackFormType.SPEND]: '/spend-management/fast-track/get-started',
  };

  return thankYouPages[fastTrackType] || thankYouPages[FastTrackFormType.PAYROLL];
}

export function getFastTrackPayload(formData: FormFieldState, type: FastTrackType): FastTrackPayload | undefined {
  const {
    [FormField.CompanyName]: companyName = '',
    [FormField.Country]: country = '',
    [FormField.CountryDialingCode]: countryDialingCode = '',
    [FormField.Email]: email = '',
    [FormField.FirstName]: firstName = '',
    [FormField.FullName]: fullName = '',
    [FormField.HasRunPayroll]: hasRunPayroll,
    [FormField.LastName]: lastName = '',
    [FormField.NumberOfPeople]: numberOfPeople = '',
    [FormField.PhoneNumber]: phoneNumber = '',
  } = formData;

  if (!companyName || !country || !email || !phoneNumber || !numberOfPeople || hasRunPayroll === undefined) {
    console.error('Missing required fields for FastTrack payload');

    return undefined;
  }

  let finalFirstName = firstName;
  let finalLastName = lastName;

  if (fullName && (!firstName || !lastName)) {
    const parsedName = parseName(fullName as string);
    finalFirstName = parsedName.firstName || '';
    finalLastName = parsedName.lastName || '';
  }

  if (!finalFirstName || !finalLastName) {
    console.error('Missing required fields for FastTrack payload');

    return undefined;
  }

  const companyHQCountry = countryDialingCode ? getCountryIso2(country as CountryName) : '1';

  return {
    companyHQCountry,
    companyName,
    countryCode: companyHQCountry,
    email,
    firstName: finalFirstName,
    hasRunPayroll: hasRunPayroll === 'Yes',
    lastName: finalLastName,
    nationalNumber: phoneNumber,
    numberOfPeople: Number(numberOfPeople),
    questionnaireType: type,
  };
}

export const isFastTrackEligible = async (payload: FastTrackPayload) => {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_APP_HUB_API_BASE_URL}/hub/api/fast_track/validate_data_from_fast_track_marketing_form/`,
      {
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      }
    );

    const json = await res.json();

    return json.decision === 'PENDING';
  } catch {
    // Log
  }

  return false;
};

export const getFastTrackData = (type: FastTrackType) => {
  return {
    llo: getFastTrackLastLeadOrigin(type),
    redirectUrl: getFastTrackThankYouPage(type),
  };
};

export async function handleFastTrack({
  country, email, fastTrackType, formFieldState, formType,
}: {
  country: string;
  email: string;
  fastTrackType: FastTrackType;
  formFieldState: FormFieldState;
  formType: FormType;
}) {
  const fastTrackPayload = getFastTrackPayload(formFieldState, fastTrackType);

  if (!fastTrackPayload) {
    sendGAEvent({
      event: GAEventName.FormError,
      form_type: formType,
    });

    return false;
  }

  if (fastTrackType === FastTrackFormType.SPEND && country && email && country === 'United States' && validateBusinessEmail(email)) {
    return true;
  }

  try {
    return await isFastTrackEligible(fastTrackPayload);
  } catch (error) {
    console.error('Error checking fast track eligibility:', error);
  }

  return false;
}
